import { plugin as UnleashPlugin, UnleashClient } from '@unleash/proxy-client-vue'

export default defineNuxtPlugin({
  name: 'unleash',
  async setup(nuxtApp) {
    const {
      public: { unleash: unleashConfig, version, mode },
    } = useRuntimeConfig()

    const unleash = reactive<{ client: UnleashClient | null; baseType: typeof UnleashClient }>({
      client: null,
      baseType: UnleashClient,
    })

    if (!unleashConfig.url || !unleashConfig.clientKey) {
      console.warn('Unleash configuration is missing. Unleash client will not be initialised.')
      return {
        provide: {
          unleash,
        },
      }
    }

    const userStore = useUserStore()
    const directSignStore = useDirectSignStore()
    const businessStore = useBusinessStore()
    const systemStore = useSystemStore()
    const { activePricePlanId } = useUserPlanInfo()

    const unleashContext = computed(() => {
      const context: UnleashContext = {
        properties: {
          appVersion: version,
        },
      }

      if (userStore.id) {
        context.userId = userStore.id
      }

      if (userStore.role) {
        context.properties = {
          ...context.properties,
          role: userStore.role,
        }
      }

      if (systemStore.platform) {
        context.properties = {
          ...context.properties,
          platform: systemStore.platform,
        }
      }

      if (businessStore.id) {
        context.properties = {
          ...context.properties,
          businessId: businessStore.id,
        }
      }

      // During the NGRI experiment, we need to add the inviting business ID to the Unleash context,
      // so the NAS users are routed correctly into the new ident flow
      if (directSignStore.isDirectSign && directSignStore.invitingBusinessId) {
        context.properties = {
          ...context.properties,
          businessId: directSignStore.invitingBusinessId,
        }
      }

      if (activePricePlanId.value) {
        context.properties = {
          ...context.properties,
          planId: activePricePlanId.value,
        }
      }

      return context
    })

    const unleashClient = new UnleashClient({
      ...unleashConfig,
      url: `${unleashConfig.url}/api/frontend`,
      environment: mode, // This is just for logging consistency, real environment is defined via API key
      context: unleashContext.value,
    })

    unleashClient.on('impression', event => {
      console.debug('Unleash impression event:', event)
      // TODO: Feed to analytics
    })

    nuxtApp.vueApp.use(UnleashPlugin, { unleashClient })

    watch(unleashContext, newContext => {
      unleashClient.updateContext(newContext)
    })

    const { logError } = useErrorHandling()

    try {
      await Promise.race([
        new Promise<void>(resolve => {
          unleashClient.on('initialized', () => {
            console.debug('Unleash client is initialised')
            resolve()
          })
        }),
        new Promise<void>((_, reject) =>
          setTimeout(() => reject(new Error('Unleash client initialisation timed out')), 1000)
        ),
      ])

      unleash.client = unleashClient
    } catch (error) {
      logError('Failed to initialise Unleash', error)
    }

    return {
      provide: {
        unleash,
      },
    }
  },
})
